import React, { useState } from 'react';
import { Box, CircularProgress, IconButton, styled } from '@mui/material';
import { getPolyglot } from '../../../../../i18n';
import { getChannelsByComposableDevice } from '../../../util/getChannelsByComposableDevice';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../redux/store.model';
import { getMachineTypeSelector } from '../../../../../redux/composableDevice/selectors/composableDeviceSelectors';
import { Merchandiser } from '../../../redux/composableDevice/model/Merchandiser.model';
import { getAllAvailableDevicesToAddByGroupIdSelector } from '../../../../../redux/commander/selectors';
import { putGroup } from '../../../../../redux/groups/actions/thunks';
import { ExternalLinkIcon, UpdateIcon } from '../../../theme/icons';
import useMerchandiserUIAction from '../MerchandiserSubheader/MerchandiserActions/MerchandiserUIAction';
import TooltipPrimary from '../../../../../theme/components/TooltipPrimary';
import { useShowErrorMessage } from '../../../../../handlingErrors';
import ControlUnitAPI from '../../../../../redux/controlUnit/api/ControlUnitAPI';
import {
  getControlUnitsActiveByIdFilter,
  getControlUnitsVisibleByIdFilter,
} from '../../../../../redux/controlUnit/selectors';

interface NoControlUnitsViewProps {
  merchandiser: Merchandiser;
}
export const NoControlUnitsView = ({
  merchandiser,
}: NoControlUnitsViewProps) => {
  const polyglot = getPolyglot();
  const dispatch = useAppDispatch();
  const showError = useShowErrorMessage();

  const machineType = useAppSelector((state) =>
    getMachineTypeSelector(state, {
      machineTypeId: merchandiser?.machineType.id,
    })
  );
  const [loading, setLoading] = useState(false);
  const [getDeviceControlUnits] =
    ControlUnitAPI.useLazyGetControlUnitByIdQuery();
  const devices =
    useAppSelector((state) =>
      getAllAvailableDevicesToAddByGroupIdSelector(state, {
        groupId: merchandiser?.parentGroupId,
      })
    ) || [];
  const selectedDevice = devices.find(
    (device) => device?.uuid === merchandiser?.deviceId
  );

  const deviceInfo = useMerchandiserUIAction({
    merchandiserId: merchandiser?.id,
  });

  const VPNIcon = {
    icon: <ExternalLinkIcon />,
    view: 'commanderVPN',
    ...deviceInfo,
  };
  const updateChannels = async () => {
    try {
      setLoading(true);
      const controlUnitsResponse = await getDeviceControlUnits(
        merchandiser.deviceId
      ).unwrap();
      const controlUnits = Object.values(controlUnitsResponse.entities)
        .filter((entity) => entity !== undefined)
        .filter(
          (entity) =>
            getControlUnitsActiveByIdFilter(entity) &&
            getControlUnitsVisibleByIdFilter(entity)
        );
      if (!controlUnits?.length) {
        return showError('freshwatch.merchandiser.add.control_unit_error');
      }
      const channelList =
        getChannelsByComposableDevice(
          machineType!,
          controlUnits,
          selectedDevice?.uuid || ''
        ) || [];
      if (!channelList.length) {
        showError('freshwatch.merchandiser.add.control_unit_error');
        return;
      }
      await dispatch(
        putGroup(merchandiser?.id ?? '', {
          ...merchandiser,
          composableDevice: {
            ...merchandiser?.composableDevice,
            channels: channelList,
          },
        })
      );
    } catch (error) {
      showError('freshwatch.merchandiser.add.control_unit_error');
    } finally {
      setLoading(false);
    }
  };
  return (
    <Box>
      {VPNIcon.showButton && (
        <TooltipPrimary title={VPNIcon.tooltip} placement="bottom">
          <span>
            <StyledIconButton
              sx={{ boxShadow: 2 }}
              key={VPNIcon.view}
              active={false}
              onClick={VPNIcon.onClick}
              color="primary"
              disabled={VPNIcon.disabled}
            >
              {VPNIcon.icon}
            </StyledIconButton>
          </span>
        </TooltipPrimary>
      )}
      <TooltipPrimary
        title={polyglot.t('freshwatch.merchandiser.update_channels')}
        placement="bottom"
      >
        <span>
          <StyledIconButton
            sx={{ boxShadow: 2 }}
            active={false}
            onClick={updateChannels}
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : <UpdateIcon />}
          </StyledIconButton>
        </span>
      </TooltipPrimary>
    </Box>
  );
};

const StyledIconButton = styled(IconButton)<{ active: boolean }>(
  ({ theme, active }) => `
      background-color: ${
        active
          ? theme.palette.secondary.main
          : theme.palette.primary.contrastText
      };
      border-radius: 1rem;
      padding: 1rem;
      height: 3.5rem;
      margin: 0.5rem;
      width: 4rem;
      &:hover {
        background-color: ${theme.palette.primary.light};
      }
      `
);
